import React, { useState } from "react";
import { Check, ZoomIn, ZoomOut, Maximize2, X, CircleCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { completionColor } from "@/lib/careerBuilder";
import { useLang, translateFr as tfr } from "@/i18n";
import { CareerA4Preview } from "@/components/career/builder/CareerA4Preview";

export const BUILDER_STEPS = [
  { id: "identity", label: tfr("Identité") },
  { id: "vision", label: "Vision" },
  { id: "roadmap", label: "Feuille de route" },
  { id: "objectives", label: "Objectifs" },
  { id: "readiness", label: "Readiness" },
  { id: "final", label: "Page finale" },
];

export function CompletionBar({ completion, saveState }) {
  const { tf } = useLang();
  if (!completion) return null;
  return (
    <div className="px-4 md:px-5 py-3 border-b border-border" data-testid="builder-completion">
      <div className="flex items-center justify-between gap-3 mb-1.5">
        <p className="text-xs font-semibold">
          {tf("Complétion du document")}{" "}
          <span className={completionColor(completion.score)} data-testid="builder-completion-score">
            {completion.score}%
          </span>
        </p>
        <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1" aria-live="polite" data-testid="builder-save-state">
          {saveState === "saving" ? tf("Enregistrement…")
            : saveState === "error" ? tf("Erreur d'enregistrement")
              : (<><CircleCheck className="h-3 w-3 text-success" /> {tf("Enregistré automatiquement")}</>)}
        </span>
      </div>
      <Progress value={completion.score} className="h-1.5" aria-label={`${tfr("Document complété à")} ${completion.score}%`} />
      {completion.missing?.length > 0 && (
        <p className="text-[11px] text-muted-foreground mt-1.5 truncate" data-testid="builder-missing">
          {tf("Encore recommandé :")} {completion.missing.slice(0, 4).join(" · ")}{completion.missing.length > 4 ? "…" : ""}
        </p>
      )}
    </div>
  );
}

export function BuilderStepNav({ current, onSelect }) {
  const { tf } = useLang();
  return (
    <nav className="px-3 md:px-4 py-2.5 border-b border-border overflow-x-auto" aria-label={tfr("Étapes du builder")}>
      <ol className="flex items-center gap-1.5 min-w-max">
        {BUILDER_STEPS.map((s, i) => {
          const n = i + 1;
          const active = n === current;
          const done = n < current;
          return (
            <li key={s.id}>
              <button
                onClick={() => onSelect(n)}
                aria-current={active ? "step" : undefined}
                data-testid={`builder-step-btn-${s.id}`}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full px-3 h-8 text-xs font-medium border transition-colors whitespace-nowrap",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-athlynx/40",
                  active ? "bg-athlynx text-white border-athlynx"
                    : "border-border text-muted-foreground hover:text-foreground hover:border-athlynx/30"
                )}
              >
                <span className={cn("inline-flex items-center justify-center", !active && done && "text-success")}>
                  {done && !active ? <Check className="h-3.5 w-3.5" /> : <span className="tabular-nums">{n}</span>}
                </span>
                {tf(s.label)}
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export function CareerPreviewPanel({ draft, meta, catalogs, mobileOpen, onMobileClose }) {
  const [zoom, setZoom] = useState(100);
  const [fullscreen, setFullscreen] = useState(false);

  const body = (inDialog) => (
    <div className="flex-1 min-h-0 overflow-auto p-4 md:p-6 bg-black/20">
      <div style={{ width: `${zoom}%`, maxWidth: inDialog ? 860 : undefined }} className="mx-auto transition-[width] duration-200 space-y-5">
        <CareerA4Preview draft={draft} meta={meta} catalogs={catalogs} />
      </div>
    </div>
  );

  const zoomCtl = (
    <div className="flex items-center gap-1">
      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setZoom((z) => Math.max(50, z - 10))}
        aria-label={tfr("Zoom arrière")} data-testid="builder-zoom-out"><ZoomOut className="h-4 w-4" /></Button>
      <span className="text-xs tabular-nums w-11 text-center" data-testid="builder-zoom-value">{zoom}%</span>
      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setZoom((z) => Math.min(150, z + 10))}
        aria-label="Zoom avant" data-testid="builder-zoom-in"><ZoomIn className="h-4 w-4" /></Button>
    </div>
  );

  return (
    <>
      <div
        className={cn(
          "flex-col min-w-0 min-h-0 rounded-2xl border border-border bg-card overflow-hidden",
          mobileOpen ? "flex fixed inset-0 z-[60] rounded-none xl:static xl:rounded-2xl" : "hidden xl:flex"
        )}
        data-testid="builder-preview-panel"
      >
        <div className="shrink-0 flex items-center justify-between gap-2 px-3.5 py-2 border-b border-border">
          <p className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">{tfr("Aperçu A4 en direct")}</p>
          <div className="flex items-center gap-1">
            {zoomCtl}
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setFullscreen(true)}
              aria-label={tfr("Plein écran")} data-testid="builder-fullscreen-btn"><Maximize2 className="h-4 w-4" /></Button>
            {mobileOpen && (
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 xl:hidden" onClick={onMobileClose}
                aria-label={tfr("Fermer l'aperçu")} data-testid="builder-mobile-close"><X className="h-4 w-4" /></Button>
            )}
          </div>
        </div>
        {body(false)}
      </div>
      <Dialog open={fullscreen} onOpenChange={setFullscreen}>
        <DialogContent className="max-w-[min(96vw,980px)] h-[92vh] p-0 flex flex-col gap-0">
          <DialogTitle className="sr-only">{tfr("Aperçu A4")}</DialogTitle>
          <div className="shrink-0 flex items-center justify-end gap-1 px-3 py-2 border-b border-border">{zoomCtl}</div>
          {body(true)}
        </DialogContent>
      </Dialog>
    </>
  );
}
